import { Loading } from '@/components';
import { getOnStorage } from '@/shared';
import { useDbFunctions, useEvents } from '@/talons';
import { tableKeys, TableTypes } from '@/types';
import styled from 'styled-components';
import { useContext, createContext, useEffect, useState, useRef, useCallback } from 'react';
import { colors } from '@/constants';
import { useTables } from './TablesContext';
import { useRemoteConfig } from './RemoteConfig';

const LoadingContainer = styled.div`
  flex: 1;
  height: 100vh;
  width: 100%;
  display: grid;
  place-items: center;
  background-color: ${colors.gray.gray0};
`;

export type Tables = (typeof tableKeys)[number];
const listTables: Tables[] = [...tableKeys];

interface LoadingContextType {
  isLoading: boolean;
  updateTable: <T extends Tables>(data: TableTypes[T], name: T) => Promise<void>;
}

const LoadingContext = createContext({} as LoadingContextType);

const LoaderProvider = ({ children }: React.PropsWithChildren) => {
  const db = useDbFunctions();
  const { setOnTables } = useTables();
  const { registerEvent } = useEvents(db);
  const getOnDB = db.getOnDB;
  const setOnDB = db.setOnDB;
  const { isLoading: isLoadingRemoteConfig } = useRemoteConfig();
  const isNotLogged = ['#/login', '#/'].includes(window.location.hash);
  const [isLoading, setIsLoading] = useState<boolean>(!isNotLogged);
  const currentLoading = useRef(false);

  useEffect(() => {
    if (currentLoading.current || isNotLogged) return;
    currentLoading.current = true;

    const token = !!getOnStorage('token_magento');
    const rcaCustomer = !!getOnStorage('rca_usuario');
    const listToRemove = [
      'ciclo_venda_cliente_cliente',
      'imagens',
      'catalogo',
      'janela_entrega',
      'pedidos_offline',
      'cliente_eventos',
      'cliente_boletos',
      'checkoutOrderData',
      'checkoutOrderItems',
      'checkoutCartTotals',
    ] as Tables[];

    const tablesToDownload = listTables.filter((table) => !listToRemove.includes(table));

    if (token && rcaCustomer) {
      setIsLoading(true);
      const loadData = async () => {
        registerEvent({
          tipoEvento: 'TABLE_CONTEXT',
          obsEvento: 'Carregando tabelas',
        });

        const listEmptyTables: string[] = [];

        for (const table of tablesToDownload) {
          await getOnDB(table)
            .then((res) => {
              const isEmpty = !res || (Array.isArray(res) ? res.length === 0 : Object.keys(res).length === 0);
              
              if (isEmpty) {
                listEmptyTables.push(table);
              }
            })
            .catch((err) => {
              console.log(err);
              listEmptyTables.push(table);
            });
        }

        setTimeout(() => {
          setIsLoading(false);
          if (listEmptyTables.length) {
            registerEvent({
              tipoEvento: 'TABLE_CONTEXT',
              obsEvento: `Tabelas vazias: ${listEmptyTables.join(', ')}`,
            });
          }
          registerEvent({
            tipoEvento: 'TABLE_CONTEXT',
            obsEvento: 'Tabelas processadas',
          });
        }, 500);
      };
      loadData();
    } else {
      setIsLoading(false);
    }
  }, []);

  const updateTable = useCallback(async <T extends Tables>(data: TableTypes[T], name: T) => {
    setOnTables(data, name, true);
    await setOnDB(data, name);
  }, []);

  return (
    <LoadingContext.Provider value={{ isLoading, updateTable }}>
      {isLoading || isLoadingRemoteConfig ? (
        <LoadingContainer>
          <Loading scale={2} />
        </LoadingContainer>
      ) : (
        children
      )}
    </LoadingContext.Provider>
  );
};

const useLoader = () => {
  const context = useContext(LoadingContext);

  if (!context) throw new Error('O context useTables só pode ser usado dentro de seu escopo. Erro ao usar hook useTables');

  return context;
};

export { LoaderProvider, useLoader };
